import getEnv from './env';
import t from 'typy'; // ES6 style import
import { CLIENT_IP } from './constants';
import { CalculationType } from '@/components/charts/constant';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import i18n from '@/plugins/i18n';

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/* This function returns the following :
If value is 0 output is : 0.00
If value is 0.5 output is : 0.50
If value is 1 output is : 1.00
If value is 10000 output is : 1.00
If value is 9500 output is : 0.95
If value is 450 output is : 0.04
If value is 'test' output is : 0.00
*/
function parseApdex(value) {
  if (value > 0 && value <= 1) return parseFloat(value).toFixed(2);
  if (value) {
    return parseFloat((!isNaN(value) ? value : 0.0) / 10000).toFixed(2);
  } else return parseFloat(value).toFixed(2);
}
function parseProcessData(value, aggrNum) {
  if (value) {
    return parseFloat((!isNaN(value) ? value : 0.0) / aggrNum).toFixed(2);
  } else return parseFloat(value).toFixed(2);
}

function getSyntheticUserIp() {
  return getEnv('VUE_APP_SYNTH_USR_IP') || CLIENT_IP;
}

function replaceSpaceInId(idString) {
  return idString.replace(/ /g, '-');
}

/**
 * @param {Object} application
 * @param {String} application.key eg. "eyduYW1lJzonTXlTUUxfRGVtbycsJ3RlYW1JRCc6JzU5ZWRkZDE1LTIwMjQtNDA1OS05OGFlLTc5YTJjZTViYTEzNCcsJ3R5cGUnOidEJywnc3ViJzonTVknfQ==.1"
 * @param {String} application.label eg. "ES_Demo"
 *
 * @returns {Object}
 * eg. {
 *  name: "ES_Demo",
 *  sub: "ES",
 *  teamID: "59eddd15-2024-4059-98ae-79a2ce5ba134",
 *  type: "D",
 * }
 */
function parseServiceObjFromApplication(application) {
  try {
    let serviceId = application.key;
    let encryptedServiceId = serviceId.split('.')[0];
    let service = atob(encryptedServiceId);
    return parseService(service);
  } catch (error) {
    console.log('error', error);
  }
}

function decodeInstanceNameFromEntityID(correlatedInstanceId) {
  try {
    const entityId = correlatedInstanceId.substring(
      correlatedInstanceId.lastIndexOf('_') + 1,
    );
    return atob(entityId);
  } catch (error) {
    console.log('error', error);
  }
}
function getTpyTableValue(data, key) {
  return t(data, key).safeObject;
}
function getApdexColor(value) {
  if (value >= 0.84) {
    return 'good';
  } else if (value > 0.68 && value < 0.84) {
    return 'fair';
  } else {
    return 'poor';
  }
}
function parseService(service) {
  try {
    // Replace single quotes with double quotes
    const replacedStr = service.replace(/'/g, '"');

    // Ensure the string starts and ends with curly braces
    if (!replacedStr.startsWith('{') || !replacedStr.endsWith('}')) {
      throw new SyntaxError('Invalid JSON format: Missing curly braces');
    }

    // Split the string into key-value pairs
    const kvs = replacedStr.substring(1, replacedStr.length - 1).split(',');

    let result = [];

    for (let kv of kvs) {
      let [key, value] = kv.split(':');

      if (!key || !value) {
        throw new SyntaxError(`Invalid key-value pair: ${kv}`);
      }

      // Add quotes to keys and values if missing
      key = addQuotes(key.trim());
      value = addQuotes(value.trim());

      result.push(`${key}:${value}`);
    }

    // Join the key-value pairs into a JSON string
    const json = `{${result.join(',')}}`;

    // Parse and return the JSON object
    return JSON.parse(json);
  } catch (error) {
    console.error('Error parsing service string:', error.message);
    return null; // Return null or handle the error as needed
  }
}

function addQuotes(data) {
  data = data?.trim();
  if (data?.charAt(0) != '"') data = '"' + data;
  if (data?.charAt(data?.length - 1) != '"') data = data + '"';
  return data;
}

function importImg(src) {
  return require(src);
}
function formatBytes(bytes, decimals = 2) {
  if (bytes <= 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

function getNearestUnit(bytes) {
  if (bytes <= 0) return 'Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return sizes[i];
}

function getDataInGB(bytes, decimals = 2, includeUnit = true) {
  let gbValue = (bytes / (1024 * 1024 * 1024)).toFixed(decimals);
  return includeUnit ? gbValue + ' ' + 'GB' : gbValue;
}

function convertBytes(valueInBytes, targetUnit) {
  const units = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const index = units.indexOf(targetUnit);
  if (index === -1) {
    console.error('Invalid target unit');
    return null;
  }

  const convertedValue = valueInBytes / Math.pow(1024, index);

  return convertedValue.toFixed(2);
}

function downloadCsv({ options, params, getDurationTime }) {
  const headers = [
    'Instance Name',
    ...options.data.series.flatMap((option) =>
      option.data.map((item) => item.x?.replace('\n', ' ')),
    ),
  ];

  const headerSet = new Set(headers);

  const data = options.data.series.map((option) => [
    option.name,
    ...option.data.map((item) => item.y),
  ]);

  const rows = [
    [i18n.global.t('aws.metricName'), options.data.title],
    [i18n.global.t('aws.connectionName'), params.subscriptionId],
    [i18n.global.t('aws.startTime'), getDurationTime.value.start],
    [i18n.global.t('aws.endTime'), getDurationTime.value.end],
    [i18n.global.t('aws.resolution'), getDurationTime.value.step],
    Array.from(headerSet),
    ...data,
  ];

  const filename = `${options.data.title}.csv`;
  const csvContent = rows.map((e) => e.join(',')).join('\n');

  // Create a Blob from the CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Create an object URL for the Blob
  const url = URL.createObjectURL(blob);

  // Create a temporary `a` element
  const link = document.createElement('a');
  link.href = url;
  link.download = filename; // Set the desired file name

  // Programmatically trigger a click event on the link
  link.click();

  // Clean up by revoking the object URL
  URL.revokeObjectURL(url);
}

function validateJson(jsonObj) {
  try {
    JSON.parse(jsonObj);
    return !!jsonObj;
  } catch (error) {
    return false;
  }
}

function getPrettyJSONStr(jsonStr, tabSize = 4) {
  try {
    return JSON.stringify(JSON.parse(jsonStr), null, tabSize);
  } catch (err) {
    return '';
  }
}

const convertToK = (value) => {
  value = Number(value);
  if (value < 1000) return value;
  else value = (value / 1000).toFixed(2);
  value += 'k';
  return value;
};

function validateURLString(urlString) {
  try {
    const url = new URL(urlString);
    const urlPattern = new RegExp(
      '^(https?:\\/\\/)?' + // validate protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+!@#$%^&*]*)*' + // validate port and path
        '(\\?[;:&-a-z\\d%_.~+=@#$!^*]*)?' + // validate query string
        '(\\#[-a-z\\d_]*)?$',
      'i', // validate fragment locator
    );

    return (
      !!urlPattern.test(urlString) &&
      (url.protocol === 'http:' || url.protocol === 'https:')
    );
  } catch (_) {
    return false;
  }
}

function getRandomUniqueId() {
  return Math.floor(Math.random() * Math.random() * Date.now());
}

function validateEmptyString(strValue) {
  if (strValue === '' || strValue === null || strValue === undefined)
    return false;
  else return true;
}

function getValidatedToolTip(content, delimiterLength) {
  return {
    content,
    disabled: !content || content.toString().length < delimiterLength,
  };
}

function toCapitalise(str) {
  // JavaScript does not have a built-in toCapitalise method
  if (str.length === 0) {
    return str;
  }
  // Capitalize the first letter and make the remaining letters lowercase
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function jsonParser(str, defaultValue = {}) {
  //convert json string into json object
  try {
    return JSON.parse(str);
  } catch (error) {
    return defaultValue;
  }
}
function convertMilliseconds(milliseconds) {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  return {
    milliseconds: milliseconds % 1000,
    seconds: seconds % 60,
    minutes: minutes % 60,
    hours: hours % 24,
    days: days,
  };
}

// Validate if expression is regex
function validateRegex(regexExpression) {
  try {
    new RegExp(regexExpression);
    return true;
  } catch (error) {
    return false;
  }
}

function convertUtcToLocalTime(
  utcDateTimeString,
  localeFormat,
  timezoneOffset,
) {
  const utcDateTime = new Date(utcDateTimeString);
  const localTime = new Date(
    utcDateTime.getTime() + timezoneOffset * 60 * 60 * 1000,
  );
  return localTime.toLocaleString(localeFormat);
}

async function copyAsync(promise, type = 'text/plain') {
  /* NOTE: Safari locks down the clipboard API to only work when triggered
         by a direct user interaction. You can't use it async in a promise.
         But! You can wrap the promise in a ClipboardItem, and give that to
         the clipboard API.
         Found this on https://developer.apple.com/forums/thread/691873
      */

  if (global.ClipboardItem && navigator.clipboard.write) {
    const text = new ClipboardItem({
      [type]: promise.then((data) => new Blob([data || ''], { type })),
    });

    await navigator.clipboard.write([text]);
  } else {
    /*  NOTE: Firefox has support for ClipboardItem and navigator.clipboard.write,
            but those are behind `dom.events.asyncClipboard.clipboardItem` preference.
            Good news is that other than Safari, Firefox does not care about
            Clipboard API being used async in a Promise.
        */

    const data = await promise;
    await navigator.clipboard.writeText(data || '');
  }
}

function getNumWithFixedDecimals(val, decimals = 2) {
  if (typeof val === 'string') {
    return +parseFloat(val).toFixed(decimals);
  }
  if (typeof val === 'number') {
    const roundOffFactor = Math.pow(10, decimals);
    return Math.round((val + Number.EPSILON) * roundOffFactor) / roundOffFactor;
  }
  return val;
}
function aggregationValue(data, aggregationNum, type) {
  if (isNaN(aggregationNum)) {
    return Math.round(data * 100) / 100;
  }
  if (type === CalculationType[0].value) {
    return Math.round((data + aggregationNum) * 100) / 100;
  }
  if (type === CalculationType[1].value) {
    return Math.round((data - aggregationNum) * 100) / 100;
  }
  if (type === CalculationType[2].value) {
    return Math.round(data * aggregationNum * 100) / 100;
  }
  if (type === CalculationType[3].value) {
    return Math.round((data / aggregationNum) * 100) / 100;
  }
  return Math.round(data * 100) / 100;
}

function customSplitString(str, delimiter) {
  //To split a string with delimiter while ignoring delimiter inside opening and closing brackets
  const parts = [];
  let currentPart = '';
  let stack = 0;

  for (let i = 0; i < str?.length; i++) {
    if (str[i] === '(') {
      stack++;
    } else if (str[i] === ')') {
      stack--;
    }

    if (str[i] === delimiter && stack === 0) {
      parts.push(currentPart.trim());
      currentPart = '';
    } else {
      currentPart += str[i];
    }
  }

  if (currentPart?.trim() !== '') {
    parts.push(currentPart?.trim());
  }

  return parts;
}

function getLocalISOTime(dateString, timezoneOffset) {
  const localTime = new Date(dateString);
  const utcTime = localTime.getTime() + localTime.getTimezoneOffset() * 60000;
  const newLocalTime = new Date(utcTime + 3600000 * timezoneOffset);

  const year = newLocalTime.getFullYear();
  const month = String(newLocalTime.getMonth() + 1).padStart(2, '0');
  const day = String(newLocalTime.getDate()).padStart(2, '0');
  const hours = String(newLocalTime.getHours()).padStart(2, '0');
  const minutes = String(newLocalTime.getMinutes()).padStart(2, '0');
  const seconds = String(newLocalTime.getSeconds()).padStart(2, '0');
  const milliseconds = String(newLocalTime.getMilliseconds()).padStart(3, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
}

dayjs.extend(utc);

// Function to convert date string to desired format
const convertDateFormat = (dateStr, outputFormat) => {
  // Parse the UTC date string
  const dateObj = dayjs.utc(dateStr);

  // Format the date as "DD MMM, YYYY - HH:MM am/pm"
  const formattedDate = dateObj.format(outputFormat);

  return formattedDate;
};

const convertCostCalculationInUsd = (value) => {
  let scaledValue = value / 10000000000;

  // Use toFixed for a fixed number of decimal places
  let formattedValue = scaledValue.toFixed(10);

  // Convert to a number and return as a string to trim unnecessary zeros, ensuring no scientific notation
  return parseFloat(formattedValue)
    .toFixed(10)
    .replace(/\.?0+$/, '');
};

function formatNumberToThousand(number) {
  if (number >= 1000) {
    // If number is greater than or equal to 1000, format it to include 'K'
    return (number / 1000).toFixed(1) + 'K';
  } else {
    // If number is less than 1000, return it as is
    return number.toString();
  }
}

function convertMsToNearestUnit(ms) {
  // Define the time units in milliseconds
  const units = [
    { unit: 'day', ms: 86400000 },
    { unit: 'hour', ms: 3600000 },
    { unit: 'minute', ms: 60000 },
    { unit: 'second', ms: 1000 },
  ];

  // Find the most suitable unit to convert to
  const suitableUnit = units.find((u) => ms >= u.ms);

  if (suitableUnit) {
    // Calculate the value in the nearest unit
    let value = (ms / suitableUnit.ms).toFixed(2); // Keeping two decimal places for precision

    // For days, we might not want to pad since they can exceed two digits
    if (suitableUnit.unit !== 'day') {
      // Split the value to separate the integer part from the decimal part
      let [integerPart, decimalPart] = value.split('.');
      // Pad the integer part if necessary (for hours, minutes, and seconds)
      integerPart = integerPart.padStart(2, '0');
      // Reconstruct the value with padding
      value = `${integerPart}.${decimalPart}`;
    }

    return `${value} ${suitableUnit.unit}${parseFloat(value) > 1 ? 's' : ''}`; // Pluralize if necessary
  } else {
    // If the value is less than a second, return in milliseconds with padding
    return `${ms.toFixed(2).toString().padStart(2, '0')} ms`;
  }
}

function convertNsToNearestUnit(durationNs) {
  return convertMsToNearestUnit(parseFloat(durationNs) / 1e6);
}

function findLowestNonZero(array) {
  const nonZeroValues = array.filter((num) => num !== 0);

  if (nonZeroValues.length === 0) {
    return null;
  }

  return Math.min(...nonZeroValues);
}

function sortWithDataType(data, key, dataType, isAsc) {
  return data.slice().sort((a, b) => {
    const valueA =
      dataType === 'string' ? a[key].toLowerCase() : Number(a[key]);
    const valueB =
      dataType === 'string' ? b[key].toLowerCase() : Number(b[key]);

    return isAsc
      ? valueA < valueB
        ? -1
        : valueA > valueB
          ? 1
          : 0 // Ascending order
      : valueB < valueA
        ? -1
        : valueB > valueA
          ? 1
          : 0; // Descending order
  });
}

function decodeBase64String(encodedString) {
  /**
   * Decodes a Base64 encoded string.
   * @param {string} encodedString - The Base64 encoded string.
   * @returns {string} - The decoded string.
   */
  try {
    return atob(encodedString);
  } catch (error) {
    console.error('Error decoding Base64 string:', error.message);
    return null;
  }
}

export {
  capitalizeFirstLetter,
  parseApdex,
  replaceSpaceInId,
  getSyntheticUserIp,
  parseServiceObjFromApplication,
  decodeInstanceNameFromEntityID,
  importImg,
  formatBytes,
  getDataInGB,
  validateJson,
  getPrettyJSONStr,
  convertToK,
  validateURLString,
  getRandomUniqueId,
  getTpyTableValue,
  getApdexColor,
  validateEmptyString,
  getValidatedToolTip,
  parseProcessData,
  toCapitalise,
  jsonParser,
  convertMilliseconds,
  validateRegex,
  convertUtcToLocalTime,
  copyAsync,
  getNumWithFixedDecimals,
  aggregationValue,
  customSplitString,
  getLocalISOTime,
  convertDateFormat,
  convertCostCalculationInUsd,
  formatNumberToThousand,
  convertBytes,
  convertMsToNearestUnit,
  convertNsToNearestUnit,
  findLowestNonZero,
  getNearestUnit,
  downloadCsv,
  sortWithDataType,
  decodeBase64String,
};
